"use client";
import React from "react";
import Image from "next/image";
import type { BlockProps } from "./blocks/registry";
import { Heading } from "../design-system/atoms/Heading";
import { Paragraph } from "../design-system/atoms/Paragraph";
import Link from "next/link";
import { resolveLegacyLink } from "../utils/linkUtils";
import { useMobileSlider } from "../hooks/useMobileSlider";

type StrapiMedia = {
  url?: string;
};

type ServiceCta = {
  link?: string;
};

type ServiceItem = {
  id?: number | string;
  titre?: string;
  descr?: string;
  icon?: StrapiMedia;
  iconHover?: StrapiMedia;
  cta?: ServiceCta;
};

type ServiceData = {
  id?: number | string;
  documentId?: string;
  titre: string;
  descr?: string;
  image?: StrapiMedia;
  service?: ServiceItem[];
};

export default function Service({ data }: BlockProps<ServiceData>) {
 

  const mappedServices =
    data.service?.map((s) => ({
      id: s.id,
      title: s.titre || "",
      descr: s.descr || "",
      iconSrc: s.icon?.url  || "/fallback-icon.svg",
      cta: s.cta?.link || "#",
      iconHoverSrc: s.iconHover?.url,
    })) ?? [];

  const imageSrc = data.image?.url;
  const { sliderRef, activeIndex, handleScroll, scrollToIndex } =
    useMobileSlider({
      itemsLength: mappedServices.length,
    });

     if (!data) return null;
  return (
    <section
      className="lms-container py-12"
      key={data.documentId}
    >

      <div className="text-center mb-8">
        <Heading as="h2" className="text-4xl lg:text-[44px] font-bold text-text">
          {data.titre.split(" ").slice(0, -1).join(" ")}{" "}
          <span className="text-secondary">
            {data.titre.split(" ").slice(-1)}
          </span>
        </Heading>
        {data.descr && <Paragraph>{data.descr}</Paragraph>}
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-0 ">
        <div className="relative">
          <div className="relative w-full h-full rounded-3xl overflow-hidden z-10">
            <Image src={imageSrc || "/fallback-image.jpg"} alt={data.titre} width={640} height={731} />
          </div>
        </div>

        {/* Liste de services */}
        <div className="w-full lg:w-1/2 bg-secondaryGreen rounded-3xl p-10">
           <div
            ref={sliderRef}
            onScroll={handleScroll}
            className="
              flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory
              lg:block lg:space-y-6 lg:overflow-visible
              [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            "
          >
            {mappedServices.map((s, i) => (
              <div
                key={s.id ?? i}
                className="
                  min-w-full snap-center
                  flex items-center flex-col xl:flex-row gap-4
                  hover:bg-white p-4 rounded-2xl border-none shadow-sm
                  hover:shadow-md transition-all duration-300
                  lg:min-w-0
                "  >
                <Image
                  src={s.iconSrc}
                  alt={s.title}
                  width={80}
                  height={80}
                  className="w-20 h-20 object-contain"
                />
                <div>
                  <Link href={resolveLegacyLink(s.cta)}>
                    <h3 className="text-secondary font-semibold text-lg mb-1 text-center xl:text-start">
                      {s.title}
                    </h3>
                  </Link>

                  <p className="text-gray-700 text-sm font-medium leading-relaxed text-center xl:text-start">
                    {s.descr}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {/* Dots mobile */}
          {mappedServices.length > 1 && (
            <div className="flex justify-center gap-2 mt-4 lg:hidden">
              {mappedServices.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => scrollToIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeIndex === index
                      ? "w-6 bg-secondary"
                      : "w-2 bg-white/70"
                  }`}
                  aria-label={`Aller au service ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

    </section>
  );
}
